import React, { useState } from "react";
import axios from "axios";
import MaterialGroupSelect from "./MaterialGroupSelect";
import MaterialLocationSelect from "./MaterialLocationSelect";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";
const inpSm =
  "border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";

const LIST_UNIT = ["ROLL", "SHEET", "PLY", "KG", "PCS", "RIM"];

function NewMaterial() {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedMatGroup, setSelectedMatGroup] = useState(null);
  const [productName, setProductName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [productUnit, setProductUnit] = useState(LIST_UNIT[0]);
  const [productLocation, setProductLocation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [checkCode, setCheckCode] = useState(false);
  const [appCode, setAppCode] = useState("");
  const [codeStatus, setCodeStatus] = useState(null); // "ok" | "taken" | null

  const handleProductCodeChange = (e) => {
    setProductCode(e.target.value);
    setCheckCode(false);
    setCodeStatus(null);
    setAppCode("");
  };

  const checkProductCode = async () => {
    if (!selectedGroup?.group_code || !productCode) {
      toast.warning("Select a group and enter a product code first.");
      return;
    }
    const fullCode = `${selectedGroup.group_code}${productCode}`;
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/api/store/store/check_product_code",
        {
          params: { product_code: fullCode },
          withCredentials: true,
        },
      );
      if (res.data.exists) {
        toast.error(`Code "${fullCode}" is already taken.`);
        setCodeStatus("taken");
        setAppCode("");
        setCheckCode(false);
      } else {
        toast.success(`Code "${fullCode}" is available.`);
        setCodeStatus("ok");
        setAppCode(fullCode);
        setCheckCode(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to check code.");
    }
  };

  const insertOrder = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        "https://eps.elkomprintama.com/post/api/store/mats",
        {
          product_name: productName,
          product_code: appCode,
          group_id: selectedMatGroup,
          unit: productUnit,
          product_location_id: productLocation,
        },
      );
      if (res.status === 200) {
        toast.success(res.data.msg);
        setProductName("");
        setProductCode("");
        setAppCode("");
        setCheckCode(false);
        setCodeStatus(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Insert failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productName || !productCode || !selectedMatGroup) {
      toast.warning("Please fill in all required fields.");
      return;
    }
    if (!checkCode) {
      toast.warning("Please check the product code first.");
      return;
    }
    setShowConfirm(true);
    setUploadPending(true);
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Uploading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-cyan-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          New Material
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-sm">
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <MaterialLocationSelect onChangeList={setProductLocation} />

          <MaterialGroupSelect
            onChangeList={setSelectedMatGroup}
            onGroupObjectChange={setSelectedGroup}
          />

          {/* Product Name */}
          <div>
            <label className={lbl}>Product Name</label>
            <input
              className={inp}
              type="text"
              placeholder="e.g. WOODFREE SIZE 106MM x 500M"
              value={productName}
              onChange={(e) => setProductName(e.target.value.toUpperCase())}
            />
          </div>

          {/* Product Code */}
          <div>
            <label className={lbl}>Product Code</label>
            <div className="flex items-center gap-2">
              {/* Group code prefix (read-only) */}
              <input
                type="text"
                disabled
                readOnly
                value={selectedGroup?.group_code || ""}
                className="w-20 border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-500 cursor-not-allowed text-center"
              />
              <span className="text-gray-400 font-bold">-</span>
              {/* Suffix input */}
              <input
                className={`flex-1 ${inpSm}`}
                type="text"
                placeholder="Code suffix"
                value={productCode}
                onChange={handleProductCodeChange}
              />
              {/* Check button */}
              <button
                type="button"
                onClick={checkProductCode}
                className="px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition whitespace-nowrap"
              >
                Check
              </button>
            </div>
            {/* Status hint */}
            {codeStatus === "ok" && (
              <p className="text-xs text-green-600 mt-1 font-semibold">
                ✓ Final code: <strong>{appCode}</strong> — available
              </p>
            )}
            {codeStatus === "taken" && (
              <p className="text-xs text-red-500 mt-1 font-semibold">
                ✗ Code{" "}
                <strong>
                  {selectedGroup?.group_code}
                  {productCode}
                </strong>{" "}
                is already taken
              </p>
            )}
            {!codeStatus && (selectedGroup?.group_code || productCode) && (
              <p className="text-xs text-gray-400 mt-1">
                Preview:{" "}
                <strong>
                  {selectedGroup?.group_code || ""}
                  {productCode}
                </strong>{" "}
                — click Check to verify
              </p>
            )}
          </div>

          {/* Unit */}
          <div>
            <label className={lbl}>Unit</label>
            <select
              className={inp}
              value={productUnit}
              onChange={(e) => setProductUnit(e.target.value)}
            >
              {LIST_UNIT.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="w-full py-3 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Insert Material
          </button>
        </form>
      </div>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Are you sure you want to insert this material?"
          onConfirm={() => {
            setShowConfirm(false);
            insertOrder();
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
}

export default NewMaterial;
